import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { pdf } from '@react-pdf/renderer'
import * as htmlToImage from 'html-to-image'
import SelectFromQuery from '~/prix/react/components/form/inputs/selectFromQuery'
import AppLayout from '~/components/appLayout'
import theme, { colors, primaryColors, secondaryTones } from '~/design'
import useGoogleMaps from '~/prix/react/hooks/googleMaps'
import useConfig from '~/prix/react/hooks/config'
import { useNavigate, useParams } from 'react-router-dom'
import useItems from '~/prix/react/hooks/items'
import AlertIcon from '~/components/icons/ui/16px_alert.svg'
import ZoomIcon from '~/components/icons/ui/16px_zoom-2.svg'
import EditIcon from '~/components/icons/ui/16px_edit-72.svg'
import useAPI from '~/prix/react/hooks/api'
import DateField from '~/prix/react/components/form/inputs/dateTimeField'
import { format, subYears, startOfYear } from 'date-fns'
import LogoSebraeApp from '~/meta/sebraeLogoApp'
import AgentReportContent from './agentReportContent'
import AgentReportPdf from './agentReportPdf.component'
import { toastInfo, toastSuccess, toastError } from '~/components/toast'
import { useAttendantVisitLocations, VisitLocation } from './hooks/useAttendantVisitLocations'
import AgentInformationQuery from './queries/agentInformation.query'
import AgentFetchSelfQuery from './queries/agentFetchSelf.query'
import AttendantListQuery from './queries/attendantList.query'
import { PulseLoader } from 'react-spinners'
import { useEvent, useMedia } from 'react-use'
import { ascending, entity, query } from '~/prix'
import { AgentReportFilter, AgentReportInitialMetricCard } from './types/agentReport.types'
import { FullscreenLoader } from '~/components/fullscreenLoader'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px 24px 24px;
  gap: 12px;
  flex: 1;

  @media (max-width: 1100px) {
    padding: 12px 16px 16px;
  }
`

const ComponentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 300px;
  width: 100%;
  padding: 26px 20px;
`

const Banner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  padding: 16px 24px 0;

  h1 {
    color: ${colors.sebraeBlueDarker};
    padding: 0;
    margin: 0;

    @media (max-width: 768px) {
      display: none;
    }
  }

  @media (max-width: 768px) {
    justify-content: center;
  }
`

const FilterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 24px 24px 12px;
  padding: 24px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: ${theme.shadow(0.75)};
  z-index: 2;

  @media (max-width: 1100px) {
    margin: 16px;
    padding: 16px;
  }
`

const FilterRow = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;

  &:first-of-type {
    @media (max-width: 768px) {
      flex-direction: column;
    }
  }
`
const FilterRowSpaceBetween = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  justify-content: space-between;

  &:first-of-type {
    @media (max-width: 768px) {
      flex-direction: column;
    }
  }
`

const FilterItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`

const LabelSelect = styled.label`
  display: block;
  margin-bottom: 8px;
`

const FilterItemCheckbox = styled.label`
  display: flex;
  align-items: center;
  gap: 5px;
`

const UfSelect = styled.select`
  width: 100%;
  height: 40px;
  border: 1px solid #cccccc;
  border-radius: 4px;
  padding: 0 12px;
  background-color: #fff;
  color: #333333;
  font-size: 14px;
  cursor: pointer;
  transition: border-color 0.2s ease-in-out;

  &:focus {
    outline: none;
    border-color: #2684ff;
  }

  &:disabled {
    background-color: #f1f1f1;
    color: #838383;
    cursor: not-allowed;
  }
`

const MessageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.darkGrey};
`

const MessageIcon = styled.div`
  margin-bottom: 8px;
`

const HeaderAgentSelected = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  position: sticky;
  top: 0px;
  z-index: 1;
  padding: 12px 24px;
  background-color: #fafafa;

  @media (max-width: 1100px) {
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 6px;
    padding: 6px 16px;
  }
`

const FilterItemSelected = styled.div<{ $color?: string }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background-color: ${props => (props.$color ? props.$color : theme.colors.mediumGrey)};
  color: ${props => (props.$color ? '#fff' : theme.colors.darkGrey)};
  border-radius: 16px;
  margin: 0;
  transition: opacity 0.2s ease-in-out;
  cursor: pointer;
  line-height: 1.25;
  opacity: 1;

  &:hover {
    opacity: 0.7;
  }

  @media (max-width: 768px) {
    padding: 4px 10px;
    font-size: 12px;
  }
`

const ExportButton = styled.button<{ $disabled: boolean }>`
  padding: 6px 20px;
  border-radius: 8px;
  border: 2px solid ${({ $disabled }) => ($disabled ? '#e0e0e0' : primaryColors.action)};
  background: white;
  color: ${({ $disabled }) => ($disabled ? '#999' : primaryColors.action)};
  font-weight: 600;
  font-size: 0.875rem;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  white-space: nowrap;
  min-width: 118px;
  min-height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    ${({ $disabled }) =>
      !$disabled &&
      `
      background: ${primaryColors.action};
      color: white;
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(0, 86, 179, 0.2);
    `}
  }

  &:active {
    ${({ $disabled }) => !$disabled && 'transform: translateY(0);'}
  }

  svg {
    width: 16px;
    height: 16px;
  }

  @media (max-width: 768px) {
    padding: 10px 16px;
    min-height: 40px;
  }
`

const LineHr = styled.hr`
  margin: 0;
  display: block;
  height: 1px;
  background: transparent;
  width: 100%;
  border: none;
  border-top: solid 1px #005eb8;
`

const ExportSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 8px 24px 24px;

  @media (max-width: 1100px) {
    padding: 8px 16px 16px;
  }
`

const ExportButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`

function validateDateRange(startDateStr: string, endDateStr: string): boolean {
  const [sy, sm, sd] = startDateStr.split('-').map(Number)
  const [ey, em, ed] = endDateStr.split('-').map(Number)
  const start = new Date(sy, (sm || 1) - 1, sd || 1)
  const end = new Date(ey, (em || 1) - 1, ed || 1)
  start.setHours(12, 0, 0, 0)
  end.setHours(12, 0, 0, 0)
  return start <= end
}

function formatDateString(dateStr: string): string {
  const [year, month, day] = dateStr.split('-')
  return `${day}/${month}/${year}`
}

function getDefaultDateRange() {
  return {
    startDate: format(startOfYear(new Date()), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
  }
}

const DATA_SOURCE_ID = 8
const reportGridId = 'agent-report-export-grid'

export default function AgentReportScreen() {
  const { context } = useAPI()
  const { google } = useGoogleMaps()
  const config = useConfig()
  const apiKey = config.services.googleConfig.apiKey

  const [attendantSelected, setAttendantSelected] = useState<{
    id: string
    name: string
    hash: string
  }>()
  const [attendantComparisonSelected, setAttendantComparisonSelected] = useState<{
    id: string
    name: string
    hash: string
  }>()
  const [startDate, setStartDate] = useState<string | null>(() => getDefaultDateRange().startDate)
  const [endDate, setEndDate] = useState<string | null>(() => getDefaultDateRange().endDate)

  const [isComparisonEnabled, setIsComparisonEnabled] = useState(false)
  const [selectedUfs, setSelectedUfs] = useState<string[]>([])
  const [currentKey, setCurrentKey] = useState(0)
  const [isPdfGenerating, setIsPdfGenerating] = useState(false)
  const [isReportReadyToExport, setIsReportReadyToExport] = useState(false)
  const [initialMetricCards, setInitialMetricCards] = useState<AgentReportInitialMetricCard[]>([])

  const navigate = useNavigate()
  const { agentHash: paramsAgentHash } = useParams()

  const inputRefs = useRef<{
    attendant: HTMLDivElement | null
    attendantComparison: HTMLDivElement | null
    startDate: HTMLDivElement | null
    endDate: HTMLDivElement | null
    uf: HTMLDivElement | null
  }>({
    attendant: null,
    attendantComparison: null,
    startDate: null,
    endDate: null,
    uf: null,
  })

  const isMobile = useMedia('max-width: 1100px')
  useEvent('resize', () => {
    setCurrentKey(prev => prev + 1)
  })

  const handleFocusInput = useCallback(
    (type: 'attendant' | 'attendantComparison' | 'startDate' | 'endDate' | 'uf') => {
      const element = inputRefs.current[type]

      if (element) {
        const input = element.querySelector('input, select') as
          | HTMLInputElement
          | HTMLSelectElement
          | null
        if (input) {
          input.focus()
        }
      }
    },
    [],
  )

  const agentInformations = useItems(
    () =>
      paramsAgentHash !== undefined ? AgentInformationQuery(paramsAgentHash) : (null as never),
    [paramsAgentHash],
    {
      cache: 60 * 60 * 24 * 7,
      autoLoad: paramsAgentHash !== undefined && !!context.user,
    },
  )

  const { validAttendances, isLoading: isLoadingAttendances } = useAttendantVisitLocations({
    agent: attendantSelected || { id: '', name: '' },
    startDate: startDate || '',
    endDate: endDate || '',
    autoLoad: !!context.user,
  })

  const {
    validAttendances: validAttendancesComparison,
    isLoading: isLoadingAttendancesComparison,
  } = useAttendantVisitLocations({
    agent: attendantComparisonSelected || { id: '', name: '' },
    startDate: startDate || '',
    endDate: endDate || '',
    autoLoad: !!context.user && isComparisonEnabled && !!attendantComparisonSelected,
  })

  const attendantListQueryFactory = useCallback(
    (input: string) => AttendantListQuery(input, context),
    [context],
  )

  const stateList = useItems(
    () =>
      query('state')
        .select({
          id: entity('state').property('id'),
          name: entity('state').property('name'),
          abbreviation: entity('state').property('abbreviation'),
        })
        .order(ascending('name')),
    [],
    {
      cache: 60 * 60 * 24 * 7,
      autoLoad: true,
    },
  )

  const allUfOptions = useMemo(() => {
    if (!stateList.items) return []

    return stateList.items
      .map((item: any) =>
        String(item.abbreviation || '')
          .trim()
          .toUpperCase(),
      )
      .filter((item: string) => item !== '')
      .sort((first: string, second: string) => first.localeCompare(second))
  }, [stateList.items])

  const ufNameMap = useMemo(() => {
    if (!stateList.items) return {}

    return stateList.items.reduce((acc: Record<string, string>, item: any) => {
      const uf = String(item.abbreviation || '')
        .trim()
        .toUpperCase()
      if (uf) {
        acc[uf] = item.name || ''
      }
      return acc
    }, {})
  }, [stateList.items])

  const allUfsSelected = useMemo(() => {
    if (allUfOptions.length === 0) return false

    return selectedUfs.length === 0
  }, [allUfOptions, selectedUfs])

  const selectedUfDropdownValue = useMemo(() => {
    if (allUfsSelected || selectedUfs.length !== 1) {
      return 'ALL'
    }

    return selectedUfs[0]
  }, [allUfsSelected, selectedUfs])

  const handleUfDropdownChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value

    if (value === 'ALL') {
      setSelectedUfs([])
      return
    }

    setSelectedUfs(value ? [value] : [])
  }, [])

  const resetAgentDependentFilters = useCallback(() => {
    const defaultDateRange = getDefaultDateRange()

    setSelectedUfs([])
    setStartDate(defaultDateRange.startDate)
    setEndDate(defaultDateRange.endDate)
  }, [])

  const handleAttendant = useCallback(
    (
      type: 'attendant' | 'attendantComparison',
      valueId: unknown,
      valueName: unknown,
      hash?: unknown,
    ) => {
      if (type === 'attendant' && typeof valueId === 'string' && typeof valueName === 'string') {
        if (attendantComparisonSelected?.id === valueId) {
          toastInfo('O agente principal deve ser diferente do agente de comparação.')
          return
        }

        setAttendantSelected({
          id: valueId,
          name: valueName,
          hash: typeof hash === 'string' ? hash : '',
        })
        navigate(`/app/agent-report/${hash}/${DATA_SOURCE_ID}`)
      } else if (type === 'attendant' && !valueId) {
        setAttendantSelected(undefined)
        setAttendantComparisonSelected(undefined)
        setIsComparisonEnabled(false)
        resetAgentDependentFilters()

        navigate('/app/agent-report')
      } else if (
        type === 'attendantComparison' &&
        typeof valueId === 'string' &&
        typeof valueName === 'string'
      ) {
        if (attendantSelected?.id === valueId) {
          toastInfo('O agente para comparação deve ser diferente do agente principal.')
          return
        }

        setAttendantComparisonSelected({
          id: valueId,
          name: valueName,
          hash: typeof hash === 'string' ? hash : '',
        })

        if (isMobile) {
          setCurrentKey(prev => prev + 1)
        }
      } else if (type === 'attendantComparison' && !valueId) {
        setAttendantComparisonSelected(undefined)
      }
    },
    [
      navigate,
      attendantSelected,
      attendantComparisonSelected,
      isMobile,
      resetAgentDependentFilters,
    ],
  )

  useEffect(() => {
    if (attendantSelected) return

    if (agentInformations.isLoading === false && paramsAgentHash !== undefined) {
      if (agentInformations.items && agentInformations.items?.length > 0) {
        const valueId = agentInformations.items[0].agentCpf
        const valueName = agentInformations.items[0].name
        const valueHash = agentInformations.items[0].hash

        setAttendantSelected({
          id: String(valueId),
          name: String(valueName),
          hash: String(valueHash),
        })
      } else if (agentInformations.query === null) {
        setAttendantSelected(undefined)
      }
    }
  }, [
    agentInformations.items,
    agentInformations.isLoading,
    agentInformations.query,
    attendantSelected,
    paramsAgentHash,
  ])

  const fetchSelf = useItems(
    () =>
      context && context.user?.roles.includes('agent')
        ? AgentFetchSelfQuery(context.user.cpf ?? '')
        : (null as never),
    [context.user?.roles],
    {
      cache: 60 * 60 * 24 * 7,
      autoLoad: !!context.user && context.user.roles.includes('agent') ? true : false,
    },
  )

  useEffect(() => {
    if (
      context.user?.roles.includes('agent') &&
      fetchSelf.isLoading === false &&
      fetchSelf.items &&
      fetchSelf.items?.length > 0
    ) {
      handleAttendant(
        'attendant',
        fetchSelf.items[0].agentCpf,
        fetchSelf.items[0].name,
        fetchSelf.items[0].hash,
      )
    }
  }, [fetchSelf.isLoading, fetchSelf.items])

  const handleStartDateChange = useCallback(
    (date: Date | null) => {
      date?.setHours(0, 0, 0, 0)
      const formattedDate = date ? format(date, 'yyyy-MM-dd') : null
      if (formattedDate && endDate && !validateDateRange(formattedDate, endDate)) {
        toastInfo('A data inicial não pode ser maior que a final.')
        return
      }
      setStartDate(formattedDate)
    },
    [endDate],
  )

  const handleEndDateChange = useCallback(
    (date: Date | null) => {
      date?.setHours(23, 59, 59, 999)
      const formattedDate = date ? format(date, 'yyyy-MM-dd') : null
      if (formattedDate && startDate && !validateDateRange(startDate, formattedDate)) {
        toastInfo('A data inicial não pode ser maior que a final.')
        return
      }
      setEndDate(formattedDate)
    },
    [startDate],
  )

  const handleComparisonToggle = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setIsComparisonEnabled(e.target.checked)
    if (!e.target.checked) {
      setAttendantComparisonSelected(undefined)
    }
  }, [])

  const waitForMilliseconds = useCallback((milliseconds: number): Promise<void> => {
    return new Promise(resolve => setTimeout(resolve, milliseconds))
  }, [])

  const scrollReportUpBeforeExport = useCallback(async (): Promise<void> => {
    const reportGridElement = document.getElementById(reportGridId)

    if (reportGridElement) {
      reportGridElement.scrollIntoView(true)

      let currentParentElement = reportGridElement.parentElement

      while (currentParentElement) {
        const currentParentStyle = window.getComputedStyle(currentParentElement)
        const canScrollVertically =
          /(auto|scroll|overlay)/.test(currentParentStyle.overflowY) &&
          currentParentElement.scrollHeight > currentParentElement.clientHeight

        if (canScrollVertically) {
          currentParentElement.scrollTop = 0
        }

        currentParentElement = currentParentElement.parentElement
      }
    }

    const scrollingElement = document.scrollingElement as HTMLElement | null
    if (scrollingElement) {
      scrollingElement.scrollTop = 0
    }
    document.documentElement.scrollTop = 0
    document.body.scrollTop = 0
    window.scrollTo(0, 0)

    await waitForMilliseconds(260)
  }, [waitForMilliseconds])

  const waitForElementReadyToExport = useCallback(
    async (element: HTMLElement): Promise<void> => {
      const hasMapContent =
        !!element.querySelector('[data-export-map-container="true"]') ||
        !!element.querySelector('.gm-style')

      if (!hasMapContent) {
        await waitForMilliseconds(80)
        return
      }

      const maxAttempts = 50

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const hasPendingMapSkeleton = !!element.querySelector('[data-export-ready="false"]')
        const mapCanvases = Array.from(element.querySelectorAll('.gm-style canvas')) as HTMLCanvasElement[]
        const hasCanvasWithSize = mapCanvases.some(canvas => canvas.width > 0 && canvas.height > 0)

        const mapTiles = Array.from(
          element.querySelectorAll('.gm-style img[src*="googleapis"], .gm-style img[src*="gstatic"]'),
        ) as HTMLImageElement[]
        const hasLoadedTile = mapTiles.some(tile => tile.complete && tile.naturalWidth > 0)

        const staticFallbackImage = element.querySelector(
          'img[data-export-static-map-image="true"]',
        ) as HTMLImageElement | null
        const hasLoadedStaticFallback =
          !!staticFallbackImage && staticFallbackImage.complete && staticFallbackImage.naturalWidth > 0

        const staticFallbackSvgContainer = element.querySelector(
          '[data-export-static-map-svg="true"]',
        ) as HTMLElement | null
        const hasSvgStaticFallback =
          !!staticFallbackSvgContainer && !!staticFallbackSvgContainer.querySelector('svg')

        const mapLooksReady =
          hasCanvasWithSize || hasLoadedTile || hasLoadedStaticFallback || hasSvgStaticFallback

        if (!hasPendingMapSkeleton && mapLooksReady) {
          await waitForMilliseconds(250)
          return
        }

        await waitForMilliseconds(120)
      }

      await waitForMilliseconds(250)
    },
    [waitForMilliseconds],
  )

  const waitForReportGridReadyToExport = useCallback(
    async (reportGridElement: HTMLElement): Promise<void> => {
      const isGridReady = () => {
        const hasSkeletonLoading = !!reportGridElement.querySelector('[data-skeleton-loading="true"]')
        const hasPendingMapCard = !!reportGridElement.querySelector('[data-export-ready="false"]')
        return !hasSkeletonLoading && !hasPendingMapCard
      }

      const maxAttempts = 50

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        if (isGridReady()) {
          await waitForMilliseconds(200)
          return
        }

        await waitForMilliseconds(150)
      }

      await waitForMilliseconds(200)
    },
    [waitForMilliseconds],
  )

  useEffect(() => {
    if (!attendantSelected || !startDate || !endDate) {
      setIsReportReadyToExport(false)
      return
    }

    const reportGridElement = document.getElementById(reportGridId)

    if (!reportGridElement) {
      setIsReportReadyToExport(false)
      return
    }

    const checkReportReadiness = () => {
      const hasSkeletonLoading = !!reportGridElement.querySelector('[data-skeleton-loading="true"]')
      const hasPendingMapCard = !!reportGridElement.querySelector('[data-export-ready="false"]')

      const hasVisibleCards = Array.from(reportGridElement.children).some(element => {
        if (!(element instanceof HTMLElement)) return false
        const bounds = element.getBoundingClientRect()
        return bounds.width > 0 && bounds.height > 0
      })

      const hasInitialCardsReady = initialMetricCards.length >= 5

      const isReady =
        !hasSkeletonLoading && !hasPendingMapCard && hasVisibleCards && hasInitialCardsReady

      setIsReportReadyToExport(prevState => (prevState === isReady ? prevState : isReady))
    }

    checkReportReadiness()

    const observer = new MutationObserver(() => {
      checkReportReadiness()
    })

    observer.observe(reportGridElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-skeleton-loading', 'data-export-ready', 'style', 'class'],
    })

    const intervalId = window.setInterval(checkReportReadiness, 300)

    return () => {
      observer.disconnect()
      window.clearInterval(intervalId)
    }
  }, [
    attendantSelected?.id,
    attendantComparisonSelected?.id,
    startDate,
    endDate,
    isComparisonEnabled,
    currentKey,
    initialMetricCards.length,
  ])

  const captureCardImage = useCallback(
    async (element: HTMLElement): Promise<Array<{ type: 'image'; src: string; width: number; height: number; cardTitle: string }>> => {
      const getCardTitle = (targetElement: HTMLElement): string => {
        return (
          targetElement.getAttribute('data-report-card-title') ||
          targetElement.querySelector('[data-report-card-title]')?.getAttribute('data-report-card-title') ||
          ''
        ).trim()
      }

      const getCrossOriginStylesheets = (): HTMLLinkElement[] => {
        return (Array.from(document.querySelectorAll('link[rel="stylesheet"]')) as HTMLLinkElement[]).filter(
          stylesheetLink => {
            const href = stylesheetLink.href || ''
            if (!href) return false

            return href.startsWith('http') && !href.startsWith(window.location.origin)
          },
        )
      }

      const shouldRetryWithoutCrossOriginStylesheets = (error: unknown): boolean => {
        const errorMessage = error instanceof Error ? error.message : String(error || '')
        const normalizedMessage = errorMessage.toLowerCase()

        return (
          normalizedMessage.includes('cssrules') ||
          normalizedMessage.includes('failed to fetch') ||
          normalizedMessage.includes('securityerror') ||
          normalizedMessage.includes('tainted')
        )
      }

      const cardTitle = getCardTitle(element)
      if (/mapa de atendimentos/i.test(cardTitle)) {
        return []
      }

      const isProductivityCard = /gr[aá]fico de produtividade/i.test(cardTitle)
      const isMatrixCard = /matriz\s+ou\s+filial/i.test(cardTitle)
      const productivityCaptureTarget = isProductivityCard
        ? (element.querySelector('[data-export-productivity-capture="true"]') as HTMLElement | null)
        : null
      const matrixCaptureTarget = isMatrixCard
        ? (element.querySelector('[data-export-matrix-wrapper="true"]') as HTMLElement | null)
        : null
      const cardContentCaptureTarget = element.querySelector(
        '[data-export-card-content="true"]',
      ) as HTMLElement | null
      const productivitySvgElement = productivityCaptureTarget?.querySelector('svg') as SVGElement | null
      const productivityChartContainer = productivitySvgElement?.parentElement as HTMLElement | null
      const captureTargetElement =
        productivityChartContainer ||
        productivityCaptureTarget ||
        matrixCaptureTarget ||
        cardContentCaptureTarget ||
        element

      const defaultCaptureOptions = {
        quality: 0.92,
        pixelRatio: 1.75,
        backgroundColor: '#ffffff',
        cacheBust: true,
        fontEmbedCSS: '',
      }

      const productivityCaptureStandardWidth = 2400
      const productivityCaptureStandardHeight = 822

      const captureOptions = isProductivityCard
        ? {
            ...defaultCaptureOptions,
            quality: 0.95,
            pixelRatio: 2,
            canvasWidth: productivityCaptureStandardWidth,
            canvasHeight: productivityCaptureStandardHeight,
            style: {
              overflow: 'visible',
            },
          }
        : isMatrixCard
          ? {
              ...defaultCaptureOptions,
              quality: 0.95,
              pixelRatio: 2,
              style: {
                overflow: 'visible',
              },
            }
          : defaultCaptureOptions

      const setupStaticMapFallbackOverlay = (): (() => void) | null => {
        const mapContainer = element.querySelector('[data-export-map-container="true"]') as HTMLElement | null
        if (!mapContainer) return null

        const staticMapSvgContainer = mapContainer.querySelector(
          '[data-export-static-map-svg="true"]',
        ) as HTMLElement | null

        const staticMapSvgElement = staticMapSvgContainer?.querySelector('svg') as SVGElement | null

        const staticMapImage = mapContainer.querySelector(
          'img[data-export-static-map-image="true"]',
        ) as HTMLImageElement | null

        const hasStaticMapImage = !!staticMapImage && !!staticMapImage.src && staticMapImage.complete
        const hasStaticMapSvg = !!staticMapSvgElement

        if (!hasStaticMapSvg && !hasStaticMapImage) return null

        const liveMapElements = Array.from(
          mapContainer.querySelectorAll('.google-map, .gm-style, canvas'),
        ) as HTMLElement[]

        const currentMapContainerPosition = mapContainer.style.position
        if (!currentMapContainerPosition) {
          mapContainer.style.position = 'relative'
        }

        const staticOverlayElement = document.createElement('div')
        staticOverlayElement.setAttribute('aria-hidden', 'true')
        staticOverlayElement.style.position = 'absolute'
        staticOverlayElement.style.inset = '0'
        staticOverlayElement.style.width = '100%'
        staticOverlayElement.style.height = '100%'
        staticOverlayElement.style.zIndex = '1'
        staticOverlayElement.style.pointerEvents = 'none'
        staticOverlayElement.style.backgroundColor = '#ffffff'

        if (hasStaticMapImage && staticMapImage) {
          const staticOverlayImage = document.createElement('img')
          staticOverlayImage.src = staticMapImage.src
          staticOverlayImage.alt = ''
          staticOverlayImage.setAttribute('aria-hidden', 'true')
          staticOverlayImage.style.position = 'absolute'
          staticOverlayImage.style.inset = '0'
          staticOverlayImage.style.width = '100%'
          staticOverlayImage.style.height = '100%'
          staticOverlayImage.style.objectFit = 'cover'
          staticOverlayElement.appendChild(staticOverlayImage)
        }

        if (hasStaticMapSvg && staticMapSvgElement) {
          const staticOverlaySvg = staticMapSvgElement.cloneNode(true) as SVGElement
          if (staticOverlaySvg) {
            staticOverlaySvg.setAttribute('width', '100%')
            staticOverlaySvg.setAttribute('height', '100%')
            staticOverlaySvg.setAttribute('preserveAspectRatio', 'xMidYMid slice')
            staticOverlaySvg.style.position = 'absolute'
            staticOverlaySvg.style.inset = '0'
            staticOverlaySvg.style.width = '100%'
            staticOverlaySvg.style.height = '100%'
            staticOverlaySvg.style.display = 'block'
            staticOverlayElement.appendChild(staticOverlaySvg)
          }
        }

        mapContainer.appendChild(staticOverlayElement)

        const liveMapElementsDisplayState = liveMapElements.map(liveMapElement => ({
          element: liveMapElement,
          display: liveMapElement.style.display,
        }))

        liveMapElements.forEach(liveMapElement => {
          liveMapElement.style.display = 'none'
        })

        return () => {
          staticOverlayElement.remove()

          liveMapElementsDisplayState.forEach(liveMapElementState => {
            liveMapElementState.element.style.display = liveMapElementState.display || ''
          })

          if (!currentMapContainerPosition) {
            mapContainer.style.position = ''
          }
        }
      }

      const setupTemporaryMatrixVerticalForPdf = (): (() => void) | null => {
        if (!isMatrixCard) return null

        const matrixWrapper = element.querySelector('[data-export-matrix-wrapper="true"]') as HTMLElement | null
        if (!matrixWrapper) return null

        const matrixChartWrappers = Array.from(
          element.querySelectorAll('[data-export-matrix-chart="true"]'),
        ) as HTMLElement[]

        const hasComparisonMatrix = matrixChartWrappers.length > 1

        const matrixEmptyState = element.querySelector('[data-export-matrix-empty="true"]') as HTMLElement | null

        const previousWrapperStyle = {
          display: matrixWrapper.style.display,
          flexDirection: matrixWrapper.style.flexDirection,
          alignItems: matrixWrapper.style.alignItems,
          justifyContent: matrixWrapper.style.justifyContent,
          gap: matrixWrapper.style.gap,
          width: matrixWrapper.style.width,
          maxWidth: matrixWrapper.style.maxWidth,
          margin: matrixWrapper.style.margin,
          height: matrixWrapper.style.height,
          minHeight: matrixWrapper.style.minHeight,
          overflow: matrixWrapper.style.overflow,
        }

        const previousChartWrapperStyle = matrixChartWrappers.map(matrixChartWrapper => ({
          matrixChartWrapper,
          width: matrixChartWrapper.style.width,
          maxWidth: matrixChartWrapper.style.maxWidth,
          padding: matrixChartWrapper.style.padding,
          margin: matrixChartWrapper.style.margin,
          display: matrixChartWrapper.style.display,
          justifyContent: matrixChartWrapper.style.justifyContent,
          alignItems: matrixChartWrapper.style.alignItems,
          transform: matrixChartWrapper.style.transform,
          transformOrigin: matrixChartWrapper.style.transformOrigin,
        }))

        const previousEmptyStateStyle = matrixEmptyState
          ? {
              width: matrixEmptyState.style.width,
              margin: matrixEmptyState.style.margin,
            }
          : null

        const matrixWrapperMinHeight = hasComparisonMatrix ? '900px' : '430px'
        const matrixWrapperWidth = '850px'
        const matrixChartWidth = '430px'
        const matrixChartMaxWidth = '430px'

        matrixWrapper.style.display = 'flex'
        matrixWrapper.style.flexDirection = 'column'
        matrixWrapper.style.alignItems = 'center'
        matrixWrapper.style.justifyContent = 'flex-start'
        matrixWrapper.style.gap = hasComparisonMatrix ? '15px' : '8px'
        matrixWrapper.style.width = matrixWrapperWidth
        matrixWrapper.style.maxWidth = 'none'
        matrixWrapper.style.margin = '0 auto'
        matrixWrapper.style.height = 'auto'
        matrixWrapper.style.minHeight = matrixWrapperMinHeight
        matrixWrapper.style.overflow = 'visible'

        matrixChartWrappers.forEach(matrixChartWrapper => {
          matrixChartWrapper.style.width = matrixChartWidth
          matrixChartWrapper.style.maxWidth = matrixChartMaxWidth
          matrixChartWrapper.style.padding = '0'
          matrixChartWrapper.style.margin = '0 auto'
          matrixChartWrapper.style.display = 'flex'
          matrixChartWrapper.style.justifyContent = 'center'
          matrixChartWrapper.style.alignItems = 'center'
          matrixChartWrapper.style.transform = 'none'
          matrixChartWrapper.style.transformOrigin = 'center center'
        })

        if (matrixEmptyState) {
          matrixEmptyState.style.width = hasComparisonMatrix ? '50%' : '100%'
          matrixEmptyState.style.margin = '0 auto'
        }

        return () => {
          matrixWrapper.style.display = previousWrapperStyle.display
          matrixWrapper.style.flexDirection = previousWrapperStyle.flexDirection
          matrixWrapper.style.alignItems = previousWrapperStyle.alignItems
          matrixWrapper.style.justifyContent = previousWrapperStyle.justifyContent
          matrixWrapper.style.gap = previousWrapperStyle.gap
          matrixWrapper.style.width = previousWrapperStyle.width
          matrixWrapper.style.maxWidth = previousWrapperStyle.maxWidth
          matrixWrapper.style.margin = previousWrapperStyle.margin
          matrixWrapper.style.height = previousWrapperStyle.height
          matrixWrapper.style.minHeight = previousWrapperStyle.minHeight
          matrixWrapper.style.overflow = previousWrapperStyle.overflow

          previousChartWrapperStyle.forEach(previousStyle => {
            previousStyle.matrixChartWrapper.style.width = previousStyle.width
            previousStyle.matrixChartWrapper.style.maxWidth = previousStyle.maxWidth
            previousStyle.matrixChartWrapper.style.padding = previousStyle.padding
            previousStyle.matrixChartWrapper.style.margin = previousStyle.margin
            previousStyle.matrixChartWrapper.style.display = previousStyle.display
            previousStyle.matrixChartWrapper.style.justifyContent = previousStyle.justifyContent
            previousStyle.matrixChartWrapper.style.alignItems = previousStyle.alignItems
            previousStyle.matrixChartWrapper.style.transform = previousStyle.transform
            previousStyle.matrixChartWrapper.style.transformOrigin = previousStyle.transformOrigin
          })

          if (matrixEmptyState && previousEmptyStateStyle) {
            matrixEmptyState.style.width = previousEmptyStateStyle.width
            matrixEmptyState.style.margin = previousEmptyStateStyle.margin
          }
        }
      }

      const applyTemporaryStyles = (
        targetElements: Array<HTMLElement | SVGElement>,
        stylesToApply: Record<string, string>,
      ): (() => void) | null => {
        if (targetElements.length === 0) return null

        const styleProperties = Object.keys(stylesToApply)
        const previousStyleState = targetElements.map(targetElement => {
          const previousStylesByProperty = styleProperties.reduce<Record<string, string>>(
            (accumulator, styleProperty) => {
              accumulator[styleProperty] = targetElement.style.getPropertyValue(styleProperty)
              return accumulator
            },
            {},
          )

          return {
            targetElement,
            previousStylesByProperty,
          }
        })

        targetElements.forEach(targetElement => {
          Object.entries(stylesToApply).forEach(([styleProperty, styleValue]) => {
            targetElement.style.setProperty(styleProperty, styleValue)
          })
        })

        return () => {
          previousStyleState.forEach(previousStyle => {
            Object.entries(previousStyle.previousStylesByProperty).forEach(([styleProperty, styleValue]) => {
              if (styleValue) {
                previousStyle.targetElement.style.setProperty(styleProperty, styleValue)
                return
              }

              previousStyle.targetElement.style.removeProperty(styleProperty)
            })
          })
        }
      }

      const setupTemporaryCaptureCleanupForPdf = (): (() => void) => {
        const restoreCallbacks: Array<() => void> = []

        const allElementNodes = [element, ...(Array.from(element.querySelectorAll('*')) as HTMLElement[])]
        const restoreGlobalVisualCleanup = applyTemporaryStyles(allElementNodes, {
          'box-shadow': 'none',
          filter: 'none',
          'text-shadow': 'none',
        })
        if (restoreGlobalVisualCleanup) {
          restoreCallbacks.push(restoreGlobalVisualCleanup)
        }

        const restoreCardShellCleanup = applyTemporaryStyles([element], {
          'background-color': 'transparent',
          'box-shadow': 'none',
          'border-radius': '0',
        })
        if (restoreCardShellCleanup) {
          restoreCallbacks.push(restoreCardShellCleanup)
        }

        const selectorsToHide = [
          '[data-report-card-header="true"]',
          '[data-report-card-alert="true"]',
          '.gm-style-cc',
          '.gmnoprint[style*="bottom"]',
        ]

        selectorsToHide.forEach(selector => {
          const targetElements = Array.from(element.querySelectorAll(selector)) as HTMLElement[]
          const restoreHiddenElements = applyTemporaryStyles(targetElements, {
            display: 'none',
            opacity: '0',
          })

          if (restoreHiddenElements) {
            restoreCallbacks.push(restoreHiddenElements)
          }
        })

        return () => {
          restoreCallbacks.reverse().forEach(restoreCallback => {
            restoreCallback()
          })
        }
      }

      const setupTemporaryProductivityScrollResetForPdf = (): (() => void) | null => {
        if (!isProductivityCard) return null

        const previousScrollLeft = captureTargetElement.scrollLeft
        const previousScrollTop = captureTargetElement.scrollTop

        captureTargetElement.scrollLeft = 0
        captureTargetElement.scrollTop = 0

        return () => {
          captureTargetElement.scrollLeft = previousScrollLeft
          captureTargetElement.scrollTop = previousScrollTop
        }
      }

      const captureWithTemporaryDisabledStylesheets = async (
        options: Parameters<typeof htmlToImage.toJpeg>[1],
      ): Promise<string> => {
        const crossOriginStylesheets = getCrossOriginStylesheets()

        crossOriginStylesheets.forEach(stylesheetLink => {
          stylesheetLink.disabled = true
        })

        try {
          return await htmlToImage.toJpeg(captureTargetElement, options)
        } finally {
          crossOriginStylesheets.forEach(stylesheetLink => {
            stylesheetLink.disabled = false
          })
        }
      }

      try {
        let src = ''
        const restoreMapFallback = setupStaticMapFallbackOverlay()
        const restoreMatrixVertical = setupTemporaryMatrixVerticalForPdf()
        const restoreProductivityScroll = setupTemporaryProductivityScrollResetForPdf()
        const restoreCaptureCleanup = setupTemporaryCaptureCleanupForPdf()
        const hasMapFallback = !!restoreMapFallback

        if (isMatrixCard) {
          await waitForMilliseconds(180)
        }

        const baseCaptureOptions = captureOptions
        const matrixCaptureDimensions = isMatrixCard
          ? (() => {
              const matrixChartElements = captureTargetElement.querySelectorAll(
                '[data-export-matrix-chart="true"]',
              )
              const hasComparisonMatrix = matrixChartElements.length > 1
              const captureTargetBounds = captureTargetElement.getBoundingClientRect()
              const matrixContentElements = Array.from(
                captureTargetElement.querySelectorAll(
                  '[data-export-matrix-chart="true"], [data-export-matrix-empty="true"]',
                ),
              ) as HTMLElement[]

              const maxContentBottom = matrixContentElements.reduce((maxBottom, matrixContentElement) => {
                const matrixContentBounds = matrixContentElement.getBoundingClientRect()
                return Math.max(maxBottom, matrixContentBounds.bottom)
              }, captureTargetBounds.bottom)

              const maxContentRight = matrixContentElements.reduce((maxRight, matrixContentElement) => {
                const matrixContentBounds = matrixContentElement.getBoundingClientRect()
                return Math.max(maxRight, matrixContentBounds.right)
              }, captureTargetBounds.right)

              const contentHeightFromChildren = Math.max(
                Math.round(maxContentBottom - captureTargetBounds.top),
                1,
              )

              const contentWidthFromChildren = Math.max(
                Math.round(maxContentRight - captureTargetBounds.left),
                1,
              )

              const contentWidth = Math.max(
                hasComparisonMatrix ? 850 : 430,
                contentWidthFromChildren,
                Math.round(captureTargetBounds.width),
                captureTargetElement.scrollWidth,
              )
              const contentHeight = Math.max(
                hasComparisonMatrix ? 900 : 430,
                contentHeightFromChildren,
                Math.round(captureTargetBounds.height),
                captureTargetElement.scrollHeight,
              )
              const matrixSafetyPaddingWidth = 24
              const matrixSafetyPaddingHeight = hasComparisonMatrix ? 120 : 72
              const dynamicCanvasWidth = (contentWidth + matrixSafetyPaddingWidth) * 2
              const dynamicCanvasHeight = (contentHeight + matrixSafetyPaddingHeight) * 2

              return {
                contentWidth: contentWidth + matrixSafetyPaddingWidth,
                contentHeight: contentHeight + matrixSafetyPaddingHeight,
                canvasWidth: dynamicCanvasWidth,
                canvasHeight: dynamicCanvasHeight,
              }
            })()
          : null

        const matrixCaptureDimensionOptions = matrixCaptureDimensions
          ? {
              width: matrixCaptureDimensions.contentWidth,
              height: matrixCaptureDimensions.contentHeight,
              canvasWidth: matrixCaptureDimensions.canvasWidth,
              canvasHeight: matrixCaptureDimensions.canvasHeight,
            }
          : {}

        const effectiveCaptureOptions = hasMapFallback
          ? {
              ...baseCaptureOptions,
              ...matrixCaptureDimensionOptions,
              filter: (node: HTMLElement | SVGElement) => {
                if (!(node instanceof HTMLElement || node instanceof SVGElement)) {
                  return true
                }

                if (!(node instanceof HTMLElement)) {
                  return true
                }

                if (
                  node.classList.contains('google-map') ||
                  node.classList.contains('gm-style') ||
                  node.matches('canvas') ||
                  !!node.closest('.google-map') ||
                  !!node.closest('.gm-style')
                ) {
                  return false
                }

                return true
              },
            }
          : {
              ...baseCaptureOptions,
              ...matrixCaptureDimensionOptions,
            }

        try {
          src = await htmlToImage.toJpeg(captureTargetElement, effectiveCaptureOptions)
        } catch (firstError) {
          if (!shouldRetryWithoutCrossOriginStylesheets(firstError)) {
            throw firstError
          }

          src = await captureWithTemporaryDisabledStylesheets(effectiveCaptureOptions)
        } finally {
          restoreCaptureCleanup()
          restoreProductivityScroll?.()
          restoreMatrixVertical?.()
          restoreMapFallback?.()
        }

        const rect = captureTargetElement.getBoundingClientRect()

        if (isMatrixCard) {
          const matrixChartElements = Array.from(
            captureTargetElement.querySelectorAll('[data-export-matrix-chart="true"]'),
          ) as HTMLElement[]

          if (matrixChartElements.length > 0) {
            const matrixChartImages: Array<{
              type: 'image'
              src: string
              width: number
              height: number
              cardTitle: string
            }> = []

            for (let matrixChartIndex = 0; matrixChartIndex < matrixChartElements.length; matrixChartIndex++) {
              const matrixChartElement = matrixChartElements[matrixChartIndex]
              const matrixChartBounds = matrixChartElement.getBoundingClientRect()
              const matrixChartWidth = Math.max(
                Math.round(matrixChartBounds.width),
                matrixChartElement.scrollWidth,
                1,
              )
              const matrixChartHeight = Math.max(
                Math.round(matrixChartBounds.height),
                matrixChartElement.scrollHeight,
                1,
              )

              const matrixChartImageOptions = {
                ...defaultCaptureOptions,
                quality: 0.95,
                pixelRatio: 2,
                width: matrixChartWidth,
                height: matrixChartHeight,
                canvasWidth: matrixChartWidth * 2,
                canvasHeight: matrixChartHeight * 2,
                style: {
                  overflow: 'visible',
                },
              }

              let matrixChartSrc = ''

              try {
                matrixChartSrc = await htmlToImage.toJpeg(matrixChartElement, matrixChartImageOptions)
              } catch (matrixChartCaptureError) {
                if (!shouldRetryWithoutCrossOriginStylesheets(matrixChartCaptureError)) {
                  throw matrixChartCaptureError
                }

                matrixChartSrc = await captureWithTemporaryDisabledStylesheets(matrixChartImageOptions)
              }

              matrixChartImages.push({
                type: 'image',
                src: matrixChartSrc,
                width: matrixChartWidth,
                height: matrixChartHeight,
                cardTitle:
                  matrixChartElements.length > 1
                    ? `${cardTitle} (${matrixChartIndex + 1})`
                    : cardTitle,
              })
            }

            return matrixChartImages
          }
        }

        return [
          {
            type: 'image',
            src,
            width:
              isProductivityCard
                ? productivityCaptureStandardWidth
                : isMatrixCard
                  ? matrixCaptureDimensions?.contentWidth || Math.round(rect.width)
                  : Math.round(rect.width),
            height:
              isProductivityCard
                ? productivityCaptureStandardHeight
                : isMatrixCard
                  ? matrixCaptureDimensions?.contentHeight || Math.round(rect.height)
                  : Math.round(rect.height),
            cardTitle,
          },
        ]
      } catch (error) {
        console.error('Erro ao capturar elemento:', error)
        return []
      }
    },
    [],
  )

  const generatePdfBlob = useCallback(
    async (): Promise<Blob | null> => {
      if (!attendantSelected || !startDate || !endDate) return null

      const reportGridElement = document.getElementById(reportGridId)

      if (!reportGridElement) {
        toastInfo('Não foi possível localizar os dados do relatório para exportar.')
        return null
      }

      const images: Array<{
        type: 'image'
        src: string
        width: number
        height: number
        cardTitle: string
      }> = []

      await waitForMilliseconds(250)

      await waitForReportGridReadyToExport(reportGridElement)

      const exportElements = Array.from(reportGridElement.children).filter(element => {
        if (!(element instanceof HTMLElement)) return false
        const bounds = element.getBoundingClientRect()
        return bounds.width > 0 && bounds.height > 0
      }) as HTMLElement[]

      for (const element of exportElements) {
        await waitForElementReadyToExport(element)
        const imagesData = await captureCardImage(element)
        if (imagesData.length > 0) {
          images.push(...imagesData)
        }
        await waitForMilliseconds(60)
      }

      if (images.length === 0) {
        toastError('Não foi possível capturar os gráficos e dados para gerar o PDF.')
        return null
      }

      const generatedAt = new Date()
      const filters: AgentReportFilter = {
        attendant: {
          name: attendantSelected.name,
        },
        dateRange: `${formatDateString(startDate)} até ${formatDateString(endDate)}`,
      }

      if (isComparisonEnabled && attendantComparisonSelected) {
        filters.secondaryAttendant = { name: attendantComparisonSelected.name }
      }

      return pdf(
        <AgentReportPdf
          generatedAt={generatedAt}
          title='Relatório interativo de agente'
          filters={filters}
          images={images}
          summaryCards={initialMetricCards}
        />,
      ).toBlob()
    },
    [
      attendantSelected,
      startDate,
      endDate,
      isComparisonEnabled,
      attendantComparisonSelected,
      initialMetricCards,
      waitForMilliseconds,
      waitForReportGridReadyToExport,
      waitForElementReadyToExport,
      captureCardImage,
    ],
  )

  const handleExportPdf = useCallback(async () => {
    if (!attendantSelected || !startDate || !endDate || isPdfGenerating) return

    try {
      await scrollReportUpBeforeExport()
      setIsPdfGenerating(true)
      await waitForMilliseconds(180)

      const pdfBlob = await generatePdfBlob()
      if (!pdfBlob) return

      const generatedAt = new Date()
      const fileName = `relatorio-agente-sse-${attendantSelected.name}${attendantComparisonSelected ? `-${attendantComparisonSelected.name}` : ''}-${format(generatedAt, 'yyyyMMdd-HHmmss')}.pdf`
      const blobUrl = window.URL.createObjectURL(pdfBlob)
      const link = window.document.createElement('a')
      link.href = blobUrl
      link.download = fileName
      window.document.body.appendChild(link)
      link.click()
      window.document.body.removeChild(link)

      window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000)
      toastSuccess('PDF exportado com sucesso!')
    } catch (error) {
      console.error(error)
      toastError('Erro ao gerar o PDF do relatório.')
    } finally {
      setIsPdfGenerating(false)
    }
  }, [
    attendantSelected,
    startDate,
    endDate,
    isPdfGenerating,
    generatePdfBlob,
  ])

  const filterAttendancesBySelectedUfs = useCallback(
    (attendances: VisitLocation[]) => {
      if (selectedUfs.length === 0) return attendances

      const selectedUfsSet = new Set(selectedUfs.map(uf => uf.toUpperCase()))

      return attendances.filter(attendance => {
        const attendanceState = String(attendance.legalEntityState || '')
          .trim()
          .toUpperCase()

        return selectedUfsSet.has(attendanceState)
      })
    },
    [selectedUfs],
  )

  const filteredMainAttendances = useMemo(
    () => filterAttendancesBySelectedUfs(validAttendances),
    [validAttendances, filterAttendancesBySelectedUfs],
  )

  const filteredComparisonAttendances = useMemo(
    () => filterAttendancesBySelectedUfs(validAttendancesComparison),
    [validAttendancesComparison, filterAttendancesBySelectedUfs],
  )

  const validAttendancesProp = useMemo(
    () =>
      isComparisonEnabled && attendantComparisonSelected
        ? [filteredMainAttendances, filteredComparisonAttendances]
        : [filteredMainAttendances],
    [
      isComparisonEnabled,
      attendantComparisonSelected,
      filteredMainAttendances,
      filteredComparisonAttendances,
    ],
  )

  const agentProp = useMemo(
    () =>
      isComparisonEnabled && attendantComparisonSelected && attendantSelected
        ? [attendantSelected, attendantComparisonSelected]
        : attendantSelected
          ? attendantSelected
          : [],
    [isComparisonEnabled, attendantSelected, attendantComparisonSelected],
  )

  const dateRangeProp = useMemo(() => ({ startDate, endDate }), [startDate, endDate])

  const isLoadingAttendancesProp = useMemo(
    () =>
      isComparisonEnabled && attendantComparisonSelected
        ? [isLoadingAttendances, isLoadingAttendancesComparison]
        : isLoadingAttendances,
    [
      isComparisonEnabled,
      attendantComparisonSelected,
      isLoadingAttendances,
      isLoadingAttendancesComparison,
    ],
  )

  const hasStartedReportSearch = !!attendantSelected && !!startDate && !!endDate

  return (
    <AppLayout title='Relatório interativo de agente' dockActive='reports-and-studies'>
      <Banner>
        <h1>Relatório interativo de agente</h1>
        <LogoSebraeApp width={200} checkColor={colors.sebraeBlueDarker} />
      </Banner>
      <FilterWrapper>
        <FilterRow>
          <FilterItem ref={el => (inputRefs.current.attendant = el)}>
            <LabelSelect>Agente</LabelSelect>
            <SelectFromQuery
              placeholder='Buscar...'
              idProperty='id'
              isSearchInput={true}
              debounceMs={500}
              preLoad={false}
              labelProperty='name'
              isDisabled={context.user?.roles.includes('agent') ? true : false}
              queryFactory={attendantListQueryFactory}
              value={
                attendantSelected?.id
                  ? { id: attendantSelected.id, name: attendantSelected.name }
                  : undefined
              }
              onDirectChange={value =>
                handleAttendant('attendant', value?.agentCpf, value?.name, value?.hash)
              }
            />
          </FilterItem>
          {isComparisonEnabled && (
            <FilterItem ref={el => (inputRefs.current.attendantComparison = el)}>
              <LabelSelect>Agente para comparar</LabelSelect>
              <SelectFromQuery
                placeholder='Buscar...'
                idProperty='id'
                isSearchInput={true}
                debounceMs={500}
                preLoad={false}
                labelProperty='name'
                isDisabled={false}
                queryFactory={attendantListQueryFactory}
                value={
                  attendantComparisonSelected?.id
                    ? {
                        id: attendantComparisonSelected.id,
                        name: attendantComparisonSelected.name,
                      }
                    : undefined
                }
                onDirectChange={value =>
                  handleAttendant('attendantComparison', value?.agentCpf, value?.name, value?.hash)
                }
              />
            </FilterItem>
          )}
        </FilterRow>
        <FilterRow>
          <FilterItem ref={el => (inputRefs.current.uf = el)}>
            <LabelSelect>Estado (UF)</LabelSelect>
            <UfSelect value={selectedUfDropdownValue} onChange={handleUfDropdownChange}>
              <option value='ALL'>Todas os estados</option>
              {allUfOptions.map(uf => (
                <option key={uf} value={uf}>
                  {ufNameMap[uf]} - {uf}
                </option>
              ))}
            </UfSelect>
          </FilterItem>
        </FilterRow>
        <FilterRow>
          <div ref={el => (inputRefs.current.startDate = el)} style={{ flex: 1 }}>
            <DateField
              key={`start-${attendantSelected?.id}`}
              id={`start-date`}
              value={startDate}
              label='Data de início'
              onDirectChange={handleStartDateChange}
              placeholder='Início'
              meta={{
                maxDate: new Date(),
                minDate: subYears(new Date(), 3),
              }}
              isRequired
            />
          </div>
          <div ref={el => (inputRefs.current.endDate = el)} style={{ flex: 1 }}>
            <DateField
              key={`end-${attendantSelected?.id}`}
              id={`end-date`}
              value={endDate || null}
              label='Data final'
              onDirectChange={handleEndDateChange}
              placeholder='Final'
              meta={{
                maxDate: new Date(),
                minDate: subYears(new Date(), 3),
              }}
              isRequired
            />
          </div>
        </FilterRow>
        <FilterRowSpaceBetween>
          <FilterItemCheckbox htmlFor='comparison-checkbox'>
            <input
              type='checkbox'
              id='comparison-checkbox'
              checked={isComparisonEnabled}
              onChange={handleComparisonToggle}
              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
            />
            Comparar com outro agente
          </FilterItemCheckbox>
        </FilterRowSpaceBetween>
      </FilterWrapper>
      {attendantSelected && startDate && endDate && (
        <HeaderAgentSelected>
          Filtros ativos:
          <FilterItemSelected
            $color={primaryColors.default}
            onClick={() => handleFocusInput('attendant')}
          >
            {attendantSelected.name}

            <EditIcon fill='#fff' width={13} height={13} />
          </FilterItemSelected>
          {isComparisonEnabled && attendantComparisonSelected && (
            <FilterItemSelected
              $color={secondaryTones[1]}
              onClick={() => handleFocusInput('attendantComparison')}
            >
              {attendantComparisonSelected.name}

              <EditIcon fill='#fff' width={13} height={13} />
            </FilterItemSelected>
          )}
          {startDate && (
            <FilterItemSelected onClick={() => handleFocusInput('startDate')}>
              De: {formatDateString(startDate)}
              <EditIcon fill={colors.darkGray} width={13} height={13} />
            </FilterItemSelected>
          )}
          {endDate && (
            <FilterItemSelected onClick={() => handleFocusInput('endDate')}>
              Até: {formatDateString(endDate)}
              <EditIcon fill={colors.darkGray} width={13} height={13} />
            </FilterItemSelected>
          )}
          <FilterItemSelected onClick={() => handleFocusInput('uf')}>
            UF: {allUfsSelected ? 'Todas' : selectedUfs[0] || 'Todas'}
            <EditIcon fill={colors.darkGray} width={13} height={13} />
          </FilterItemSelected>
        </HeaderAgentSelected>
      )}
      <Wrapper>
        {attendantSelected && startDate && endDate ? (
          <AgentReportContent
            key={currentKey}
            agent={agentProp}
            dateRange={dateRangeProp}
            google={google}
            apiKey={apiKey}
            selectedUfs={selectedUfs}
            validAttendances={validAttendancesProp}
            isLoadingAttendances={isLoadingAttendancesProp}
            reportGridId={reportGridId}
            onInitialMetricCardsChange={setInitialMetricCards}
          />
        ) : !startDate || !endDate ? (
          <ComponentWrapper>
            <MessageWrapper>
              <MessageIcon>
                <AlertIcon fill={theme.colors.mediumGrey} width={48} height={48} />
              </MessageIcon>
              <p>Selecione uma data de início e uma data final para visualizar as informações.</p>
            </MessageWrapper>
          </ComponentWrapper>
        ) : agentInformations.isLoading ||
          isLoadingAttendancesProp ||
          (!!paramsAgentHash && !attendantSelected) ? (
          <ComponentWrapper>
            <MessageWrapper>
              <MessageIcon>
                <PulseLoader color={theme.colors.mediumGrey} size={10} />
              </MessageIcon>
              <p>Carregando...</p>
            </MessageWrapper>
          </ComponentWrapper>
        ) : (
          <ComponentWrapper>
            {context.user?.roles.includes('agent') &&
            fetchSelf.isLoading === false &&
            fetchSelf.items &&
            fetchSelf.items?.length === 0 ? (
              <MessageWrapper>
                <MessageIcon>
                  <AlertIcon fill={theme.colors.mediumGrey} width={48} height={48} />
                </MessageIcon>
                <p>Não foi possível localizar dados do agente.</p>
              </MessageWrapper>
            ) : (
              <MessageWrapper>
                <MessageIcon>
                  <ZoomIcon fill={theme.colors.mediumGrey} width={48} height={48} />
                </MessageIcon>
                <p>Escolha um agente para visualizar as informações.</p>
              </MessageWrapper>
            )}
          </ComponentWrapper>
        )}
      </Wrapper>
      {hasStartedReportSearch && (
        <ExportSection>
          <ExportButtonRow>
            <ExportButton
              onClick={handleExportPdf}
              $disabled={isPdfGenerating || !isReportReadyToExport}
              disabled={isPdfGenerating || !isReportReadyToExport}
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                />
              </svg>
              Exportar
            </ExportButton>
          </ExportButtonRow>
        </ExportSection>
      )}
      <FullscreenLoader isActive={isPdfGenerating} message='Gerando PDF ...' />
    </AppLayout>
  )
}
